import React, { useState } from 'react';
import {
  Box,
  Alert,
  Stack,
  Slider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';

const UuidUtility: React.FC = () => {
  const [uuids, setUuids] = useState<string[]>(() => {
    const saved = localStorage.getItem('uuid-output');
    return saved ? JSON.parse(saved) : [];
  });
  const [count, setCount] = useState(1);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    localStorage.setItem('uuid-output', JSON.stringify(uuids));
  }, [uuids]);

  const generateUuid = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const handleGenerate = () => {
    const newUuids = Array.from({ length: count }, () => generateUuid());
    setUuids(newUuids);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(uuids.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopySingle = async (uuid: string) => {
    try {
      await navigator.clipboard.writeText(uuid);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const clearAll = () => {
    setUuids([]);
    setCopied(false);
  };

  return (
    <Box>
      <UtilityHeader 
        title="UUID Tool" 
        description="Generate random, unique identifiers (version 4) for your projects." 
      />

      {copied && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
          Copied to clipboard!
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Count: {count}
        </Typography>
        <Slider
          value={count}
          onChange={(_, newValue) => setCount(newValue as number)}
          min={1}
          max={50}
          valueLabelDisplay="auto"
          sx={{ maxWidth: 400 }}
          size="small"
        />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 4 }} flexWrap="wrap" gap={2}>
        <GlowButton
          size="large"
          onClick={handleGenerate}
          startIcon={<Refresh />}
          sx={{ px: 4 }}
        >
          Generate
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          size="large"
          onClick={handleCopy}
          disabled={uuids.length === 0}
          startIcon={<ContentCopy />}
          sx={{ px: 4 }}
        >
          Copy All
        </GlowButton>
        <Box sx={{ flexGrow: 1 }} />
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={uuids.length === 0}
          sx={{ opacity: 0.7 }}
        >
          Clear
        </GlowButton>
      </Stack>

      {uuids.length > 0 && (
        <StyledTextArea
          label="Generated IDs"
          rows={Math.min(uuids.length + 1, 15)}
          value={uuids.join('\n')}
          InputProps={{
            readOnly: true,
            sx: { 
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.9rem',
              bgcolor: 'rgba(0,0,0,0.4)',
              borderRadius: 3,
              color: 'primary.main',
              '& fieldset': { borderColor: 'primary.main' },
            },
            endAdornment: uuids.length === 1 && (
              <Tooltip title="Copy">
                <IconButton onClick={() => handleCopySingle(uuids[0])} edge="end" sx={{ color: 'primary.main' }}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default UuidUtility;
